import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, useRef } from "react";
import { Node, Edge } from "reactflow";
import { getArchitecture } from "../api/architectures";
import { useAuth } from "./AuthContext";

// Interface pour un onglet d'architecture ouvert
export interface ArchitectureTab {
  id: string;
  projectId: string;
  projectName?: string; // Nom du projet
  name: string;
  provider: "azure" | "aws" | "gcp" | "fabric";
  architectureType?: string; // Type d'architecture (Standard, etc.)
  nodes: Node[];
  edges: Edge[];
  isDirty: boolean; // Modifications non sauvegardées
  isLoading: boolean;
  error: string | null;
}

// Type du contexte
interface ArchitectureTabsContextType {
  tabs: ArchitectureTab[];
  activeTabId: string | null;

  // Actions
  openTab: (architectureId: string, projectId: string, projectName?: string, architectureType?: string) => Promise<void>;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  updateTabData: (tabId: string, nodes: Node[], edges: Edge[]) => void;
  markTabDirty: (tabId: string, isDirty: boolean) => void;
  updateTabName: (tabId: string, name: string) => void;
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  clearAllTabs: () => void;

  // Getters
  getActiveTab: () => ArchitectureTab | null;
  isTabOpen: (architectureId: string) => boolean;
}

const ArchitectureTabsContext = createContext<ArchitectureTabsContextType | undefined>(undefined);

// Hook pour utiliser le contexte
export const useArchitectureTabs = () => {
  const context = useContext(ArchitectureTabsContext);
  if (!context) {
    throw new Error("useArchitectureTabs must be used within ArchitectureTabsProvider");
  }
  return context;
};

// Provider
export const ArchitectureTabsProvider = ({ children }: { children: ReactNode }) => {
  const [tabs, setTabs] = useState<ArchitectureTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const { user } = useAuth();
  const previousUserIdRef = useRef<string | null>(null);

  // Helper: Générer la clé localStorage pour une architecture
  const getStorageKey = useCallback((architectureId: string, userId: string | null = user?.id || null): string => {
    if (!userId) return `architecture.${architectureId}.canvas`; // Fallback (ne devrait pas arriver)
    return `architecture.${userId}.${architectureId}.canvas`;
  }, [user?.id]);

  // Helper: Nettoyer les clés localStorage d'un utilisateur spécifique
  const cleanUserArchitectures = useCallback((userId: string | null) => {
    const keysToRemove: string[] = [];
    const prefix = userId ? `architecture.${userId}.` : 'architecture.';

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);

  // Nettoyer et réinitialiser lors du changement d'utilisateur
  useEffect(() => {
    const currentUserId = user?.id || null;
    const previousUserId = previousUserIdRef.current;

    // Si l'utilisateur a changé (connexion/déconnexion/switch)
    if (currentUserId !== previousUserId) {
      // Nettoyer les onglets React
      setTabs([]);
      setActiveTabId(null);

      // Nettoyer le localStorage de l'ancien utilisateur
      if (previousUserId) {
        cleanUserArchitectures(previousUserId);
      }

      // Si déconnexion complète, nettoyer tout
      if (!currentUserId) {
        cleanUserArchitectures(null);
      }

      // Mettre à jour la ref
      previousUserIdRef.current = currentUserId;
    }
  }, [user?.id, cleanUserArchitectures]);

  // Listener pour synchroniser entre onglets
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Si une clé architecture d'un autre utilisateur est modifiée, ignorer
      if (e.key && e.key.startsWith('architecture.') && user?.id) {
        const expectedPrefix = `architecture.${user.id}.`;
        if (!e.key.startsWith(expectedPrefix)) {
          // C'est une architecture d'un autre utilisateur, ignorer
          return;
        }
      }

      // Si c'est notre utilisateur et que des données ont changé, on pourrait recharger
      // Pour l'instant on ne fait rien, mais on pourrait synchroniser les tabs entre onglets
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user?.id]);

  // Ouvrir un nouvel onglet ou activer un onglet existant
  const openTab = useCallback(async (architectureId: string, projectId: string, projectName?: string, architectureType?: string) => {
    // Utiliser le setter fonctionnel pour éviter les problèmes de closure stale
    let shouldLoad = false;

    setTabs(prev => {
      // Vérifier si l'onglet est déjà ouvert
      const existingTab = prev.find(t => t.id === architectureId);
      if (existingTab) {
        return prev; // Ne pas modifier, juste activer
      }

      shouldLoad = true;

      // Créer un nouvel onglet en état de chargement
      const newTab: ArchitectureTab = {
        id: architectureId,
        projectId,
        projectName,
        architectureType,
        name: "Chargement...",
        provider: "azure",
        nodes: [],
        edges: [],
        isDirty: false,
        isLoading: true,
        error: null,
      };

      return [...prev, newTab];
    });

    setActiveTabId(architectureId);

    // Si l'onglet existait déjà, on ne charge pas
    if (!shouldLoad) return;

    try {
      // Charger les données de l'architecture
      const architecture = await getArchitecture(architectureId);
      const diagramData = architecture.diagram_data || { nodes: [], edges: [] };

      setTabs(prev => prev.map(tab =>
        tab.id === architectureId
          ? {
              ...tab,
              name: architecture.name,
              provider: architecture.provider,
              architectureType: architectureType || tab.architectureType || 'Standard',
              nodes: diagramData.nodes || [],
              edges: diagramData.edges || [],
              isLoading: false,
              error: null,
            }
          : tab
      ));
    } catch (error) {
      // En cas d'erreur, essayer de charger depuis localStorage
      const localKey = getStorageKey(architectureId);
      const localData = localStorage.getItem(localKey);

      if (localData) {
        try {
          const parsed = JSON.parse(localData);
          setTabs(prev => prev.map(tab =>
            tab.id === architectureId
              ? {
                  ...tab,
                  name: parsed.name || "Architecture (local)",
                  nodes: parsed.nodes || [],
                  edges: parsed.edges || [],
                  isLoading: false,
                  error: null,
                }
              : tab
          ));
          return;
        } catch {
          // Ignore parse errors
        }
      }

      setTabs(prev => prev.map(tab =>
        tab.id === architectureId
          ? {
              ...tab,
              isLoading: false,
              error: "Impossible de charger l'architecture",
            }
          : tab
      ));
    }
  }, [getStorageKey]);

  // Fermer un onglet
  const closeTab = useCallback((tabId: string) => {
    setTabs(prev => {
      const newTabs = prev.filter(t => t.id !== tabId);

      // Si on ferme l'onglet actif, activer le précédent ou le suivant
      if (activeTabId === tabId && newTabs.length > 0) {
        const closedIndex = prev.findIndex(t => t.id === tabId);
        const newActiveIndex = Math.min(closedIndex, newTabs.length - 1);
        setActiveTabId(newTabs[newActiveIndex].id);
      } else if (newTabs.length === 0) {
        setActiveTabId(null);
      }

      return newTabs;
    });
  }, [activeTabId]);

  // Définir l'onglet actif
  const setActiveTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  // Mettre à jour les données d'un onglet (nodes/edges)
  const updateTabData = useCallback((tabId: string, nodes: Node[], edges: Edge[]) => {
    setTabs(prev => prev.map(tab =>
      tab.id === tabId
        ? { ...tab, nodes, edges, isDirty: true }
        : tab
    ));
  }, []);

  // Marquer un onglet comme modifié ou sauvegardé
  const markTabDirty = useCallback((tabId: string, isDirty: boolean) => {
    setTabs(prev => prev.map(tab =>
      tab.id === tabId
        ? { ...tab, isDirty }
        : tab
    ));
  }, []);

  // Mettre à jour le nom d'un onglet
  const updateTabName = useCallback((tabId: string, name: string) => {
    setTabs(prev => prev.map(tab =>
      tab.id === tabId
        ? { ...tab, name }
        : tab
    ));
  }, []);

  // Réorganiser les onglets (drag & drop)
  const reorderTabs = useCallback((fromIndex: number, toIndex: number) => {
    setTabs(prev => {
      const newTabs = [...prev];
      const [movedTab] = newTabs.splice(fromIndex, 1);
      newTabs.splice(toIndex, 0, movedTab);
      return newTabs;
    });
  }, []);

  // Obtenir l'onglet actif
  const getActiveTab = useCallback(() => {
    return tabs.find(t => t.id === activeTabId) || null;
  }, [tabs, activeTabId]);

  // Vérifier si un onglet est déjà ouvert
  const isTabOpen = useCallback((architectureId: string) => {
    return tabs.some(t => t.id === architectureId);
  }, [tabs]);

  // Fermer tous les onglets et nettoyer localStorage (utilisé lors de la déconnexion)
  const clearAllTabs = useCallback(() => {
    setTabs([]);
    setActiveTabId(null);

    // Nettoyer toutes les données d'architectures de l'utilisateur actuel
    if (user?.id) {
      cleanUserArchitectures(user.id);
    } else {
      // Si pas d'utilisateur, nettoyer tout (fallback)
      cleanUserArchitectures(null);
    }
  }, [user?.id, cleanUserArchitectures]);

  return (
    <ArchitectureTabsContext.Provider
      value={{
        tabs,
        activeTabId,
        openTab,
        closeTab,
        setActiveTab,
        updateTabData,
        markTabDirty,
        updateTabName,
        reorderTabs,
        getActiveTab,
        isTabOpen,
        clearAllTabs,
      }}
    >
      {children}
    </ArchitectureTabsContext.Provider>
  );
};

export default ArchitectureTabsContext;